import { io, type Socket } from 'socket.io-client';
import { getApiBaseUrl } from './apiConfig';

// Auth flows via the HttpOnly `showliner_session` cookie now (see
// docs/plans/AUTH_COOKIE_MIGRATION.md). socket.io-client carries
// cookies on the handshake when `withCredentials: true` — same
// behavior as `XMLHttpRequest.withCredentials` for the underlying
// long-poll / upgrade requests. Backend's socketAuthMiddleware reads
// `showliner_session` from the handshake `Cookie` header (PR1).
//
// We deliberately no longer pass `auth: { token }` — the JWT isn't
// in JS-accessible storage anymore, and the cookie is the source of
// truth. The backend keeps the auth.token fallback for transition
// safety; PR3 removes it.

// In dev: getApiBaseUrl() returns '' → io connects to window.location.origin (Vite proxy)
// In prod: getApiBaseUrl() returns the API URL (e.g., https://api.showliner.io)
const socket: Socket = io(getApiBaseUrl() || undefined, {
  autoConnect: false,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 10000,
  transports: ['websocket', 'polling'],
  withCredentials: true,
});

socket.on('connect', () => {
  console.log('[Socket] Connected:', socket.id, 'transport:', socket.io.engine?.transport?.name);
});
socket.on('disconnect', (reason) => {
  console.warn('[Socket] Disconnected:', reason);
});
socket.on('connect_error', (error) => {
  console.error('[Socket] Connection error:', error.message);
});

/**
 * Force a fresh socket handshake. Called after login/logout/register
 * so the new cookie state takes effect on an already-open socket.
 *
 * The `_token` arg is kept for backwards compatibility with existing
 * callsites in UserContext but is unused — auth comes from the
 * cookie now. PR3 removes the parameter.
 */
export const reconnectWithToken = (_token?: string): void => {
  if (socket.connected) {
    socket.disconnect();
  }
  socket.connect();
};

export default socket;
